// =====================================================================
// commande-prete/regles.js — Commande prête Nespart (13-10-2026)
// =====================================================================
// Bornes de plausibilité et messages de saisie du PDA — IDENTIQUES à la
// maquette validée (flux-commande-prete.html, « verifChamp »). Le PDA affiche
// ces messages AVANT tout appel serveur ; le serveur (ready-order.rules.js)
// refuse de son côté toute valeur hors bornes : il reste le garde-fou.
// Fonctions PURES (aucun DOM) : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

export const BORNES = { PAL: { h: [20, 260], kg: [10, 1500] }, COL: { l: [5, 200], w: [5, 200], h: [1, 200], kg: [0.1, 70] }, FMT: [40, 250] };
export const NOMS = { l: 'longueur', w: 'largeur', h: 'hauteur', kg: 'poids' };
export const UNITE = { l: 'cm', w: 'cm', h: 'cm', kg: 'kg' };
export const FORMATS = { EUR: { l: 120, w: 80 }, IND: { l: 120, w: 100 } };
export const MAX_UNITS = 60;
export const MAX_PHOTOS = 8;
export const GROS_AJOUT = 10;   // au-delà, on demande confirmation (« 4 » + « 5 » = 45 tapé par erreur)
export const MAX_REF = 50;      // réf. de chargement (ODP) : 1 à 50 caractères

// Nombre tapé : chiffres, une virgule OU un point décimal. « 1e3 », « 18 kg », « -5 » → null.
export const num = (v) => { const t = String(v ?? '').trim(); return /^\d+(?:[.,]\d+)?$/.test(t) ? Number(t.replace(',', '.')) : null; };
export const fmtNum = (n) => Number(n).toLocaleString('fr-BE', { maximumFractionDigits: 1 });
export const fmtKg = (n) => Number(n).toLocaleString('en-GB', { maximumFractionDigits: 1 });
// Valeur à TAPER (sans espace des milliers, que le champ refuserait) : 1180,3 et non « 1 180,3 ».
export const aTaper = (x) => String(x).replace('.', ',');

// Champs mesurés d'une fiche, dans l'ordre de saisie : palette = hauteur + poids
// (longueur × largeur viennent du format de la commande) ; colis = L, l, H, poids.
export const champs = (u) => (u.type === 'PAL' ? ['h', 'kg'] : ['l', 'w', 'h', 'kg']);

// Fiche sans aucune donnée (un espace oublié ne compte pas) : la seule que « − » peut retirer.
export const isEmpty = (u) => !String(u.sscc ?? '').trim() && !String(u.h ?? '').trim() && !String(u.kg ?? '').trim()
  && !String(u.l ?? '').trim() && !String(u.w ?? '').trim() && !u.photos;

// Format palette (une fois par commande) : EUR 120 × 80, IND 120 × 100, AUTRE = L et l saisis.
export function formatEtat(st) {
  if (st.format !== 'AUTRE') return { ok: true, ...(FORMATS[st.format] || FORMATS.EUR) };
  const l = num(st.fl), w = num(st.fw), [a, b] = BORNES.FMT;
  if (l === null || w === null) return { ok: false, msg: 'Format palette : indiquez la longueur et la largeur (en chiffres).' };
  if (l < a || l > b || w < a || w > b) return { ok: false, msg: `Format palette : longueur et largeur entre ${a} et ${b} cm.` };
  // Centimètres ENTIERS, comme les autres dimensions (le serveur refuse 120,5).
  if (!Number.isInteger(l) || !Number.isInteger(w)) return { ok: false, msg: 'Format palette : longueur et largeur en centimètres entiers.' };
  return { ok: true, l, w };
}

// Message de saisie d'un champ mesuré. { manque: true } = vide ; { msg } = à vérifier ; {} = bon.
export function verifChamp(u, k) {
  const brut = String(u[k] ?? '').trim();
  if (!brut) return { manque: true };
  const [a, b] = BORNES[u.type][k];
  const dans = (x) => x >= a && x <= b;
  const plage = `entre ${fmtNum(a)} et ${fmtNum(b)}`;
  // Un exemple n'est proposé que s'il est lui-même accepté ; sinon on donne la plage.
  const exemple = (x) => (dans(x) ? `ex. ${aTaper(x)}` : plage);
  const n = num(brut);
  if (n === null) return { msg: `${NOMS[k]} « ${brut} » : chiffres uniquement (${exemple(UNITE[k] === 'cm' ? 145 : u.type === 'PAL' ? 305.5 : 18.2)})` };
  const horsBornes = { msg: `${NOMS[k]} « ${brut} » ${UNITE[k]} ? (${plage})` };
  if (UNITE[k] === 'cm') {
    // Dimensions en centimètres ENTIERS. Une petite valeur avec virgule peut être des mètres
    // (« 1,45 » → 145 cm) ou des centimètres (« 7,5 » cm) : on ne propose que la lecture qui
    // tient dans les limites. 2-3 décimales (« 1,45 », « 1.180 ») = un mètre ruban → mètres ;
    // une seule décimale et les deux lectures possibles (« 1,5 ») → on pose la question.
    const cm = Math.round(n * 100), entier = Math.round(n);
    const enM = dans(cm), enCm = dans(n);
    const decimales = (brut.split(/[.,]/)[1] || '').length;
    if (n < 10 && /[.,]/.test(brut)) {
      if (enM && decimales >= 2) return { msg: `${NOMS[k]} « ${brut} » : en mètres ? Tapez ${cm} (cm)` };
      if (enM && enCm) return { msg: `${NOMS[k]} « ${brut} » : ${cm} cm (en mètres) ou ${entier} cm ? Tapez la valeur en centimètres entiers` };
      if (enM) return { msg: `${NOMS[k]} « ${brut} » : en mètres ? Tapez ${cm} (cm)` };
      if (enCm) return { msg: `${NOMS[k]} « ${brut} » : en centimètres entiers (${exemple(entier)})` };
      return { msg: `${NOMS[k]} « ${brut} » : hors limites, en cm comme en mètres (${plage})` };
    }
    if (n < a && enM) return { msg: `${NOMS[k]} « ${brut} » : en mètres ? Tapez ${cm} (cm)` };
    if (!/^\d+$/.test(brut)) return { msg: `${NOMS[k]} « ${brut} » : en centimètres entiers (${exemple(entier)})` };
    return enCm ? {} : horsBornes;
  }
  // Poids. « 1.180 » ou « 1,180 » : 1 180 (milliers) ou 1,18 (balance à 3 décimales) ? On ne
  // propose que la lecture qui tient dans les bornes ; « 0.050 » n'est jamais une lecture en milliers.
  if (/^[1-9]\d{0,2}[.,]\d{3}$/.test(brut)) {
    const mille = Number(brut.replace(/[.,]/, '')), dec = Math.round(n * 10) / 10;
    if (dans(mille)) return { msg: `${NOMS[k]} « ${brut} » = ${fmtNum(mille)} kg ? Tapez ${mille} (sans séparateur)` };
    if (dans(dec)) return { msg: `${NOMS[k]} « ${brut} » = ${fmtNum(dec)} kg ? Tapez ${aTaper(dec)}` };
    return horsBornes;
  }
  // Une décimale au plus : ce qui est saisi = ce qui part dans le mail (arrondi à 0,1).
  const arrondi = Math.round(n * 10) / 10;
  if (Math.abs(n * 10 - Math.round(n * 10)) > 1e-9) return { msg: `${NOMS[k]} « ${brut} » : un seul chiffre après la virgule (${exemple(arrondi)})` };
  return dans(n) ? {} : horsBornes;
}

// ---------- Réf. de chargement (ODP) : SEUL identifiant de la commande ----------
// Décision Silviu 14/10 : plus de n° de commande séparé. La réf. de chargement (ex. BROUT117200)
// est scannée d'un document (ou tapée), normalisée trim + MAJUSCULES, 1-50 caractères, sans « ; »
// (séparateur du fichier .097 EffiTrace) ni caractère invisible (retour à la ligne, tabulation,
// séparateur GS d'un lecteur…). Messages IDENTIQUES au serveur (ready-order.rules.js, lireRef).
const CONTROLE = /[\u0000-\u001f\u007f]/;
export const normRefChargement = (raw) => String(raw ?? '').trim().toUpperCase();
export function verifRefChargement(raw) {
  const v = normRefChargement(raw);
  if (!v) return 'Réf. chargement manquante';
  if (v.length > MAX_REF) return `Réf. chargement : ${MAX_REF} caractères maximum (${v.length} lus)`;
  if (v.includes(';')) return 'Réf. chargement : le caractère « ; » est interdit';
  if (CONTROLE.test(v)) return 'Réf. chargement : caractère invisible reçu (scanner ?) — rescannez ou tapez la référence';
  return null;
}
// Avertissement NON bloquant : la réf. attendue commence par BROUT — sur la forme normalisée
// (majuscules), comme le serveur (ready-order.rules.js, refInhabituelle).
export const refInhabituelle = (raw) => {
  const v = normRefChargement(raw);
  return !!v && !v.startsWith('BROUT');
};
export const MSG_REF_INHABITUELLE = 'Réf. inhabituelle (attendu BROUT…)';
